import React from 'react';
import {
  Server, Database, Cloud, Shield, Activity, Zap, ArrowRight, ArrowDown,
  Eye, Lock, Container, HardDrive, GitBranch, Box, FileCode,
  Network, Globe, Workflow, BarChart3, Cpu, ArrowDownRight, ArrowUpRight
} from 'lucide-react';
import Project1FlowDiagram from './Project1FlowDiagram';
import Project2FlowDiagram from './Project2FlowDiagram';
import Project3FlowDiagram from './Project3FlowDiagram';
import Project4FlowDiagram from './Project4FlowDiagram';
import Project5FlowDiagram from './Project5FlowDiagram';
import Project6FlowDiagram from './Project6FlowDiagram';
import Project7FlowDiagram from './Project7FlowDiagram';
import Project8FlowDiagram from './Project8FlowDiagram';
import Project9FlowDiagram from './Project9FlowDiagram';
import Project10FlowDiagram from './Project10FlowDiagram';
import Project11FlowDiagram from './Project11FlowDiagram';
import Project12FlowDiagram from './Project12FlowDiagram';
import Project13FlowDiagram from './Project13FlowDiagram';

// Reusable Service Component for flowchart nodes
const ServiceNode = ({ name, icon: Icon, color, delay = 0, id, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-20 h-16 text-[10px]',
    md: 'w-24 h-18 text-xs',
    lg: 'w-28 h-20 text-sm'
  };

  const colorClasses = {
    blue: 'bg-gradient-to-br from-blue-100 to-blue-200 border-blue-400 text-blue-800 shadow-blue-200',
    green: 'bg-gradient-to-br from-green-100 to-green-200 border-green-400 text-green-800 shadow-green-200',
    purple: 'bg-gradient-to-br from-purple-100 to-purple-200 border-purple-400 text-purple-800 shadow-purple-200',
    orange: 'bg-gradient-to-br from-orange-100 to-orange-200 border-orange-400 text-orange-800 shadow-orange-200',
    red: 'bg-gradient-to-br from-red-100 to-red-200 border-red-400 text-red-800 shadow-red-200',
    yellow: 'bg-gradient-to-br from-yellow-100 to-yellow-200 border-yellow-400 text-yellow-800 shadow-yellow-200',
    gray: 'bg-gradient-to-br from-gray-100 to-gray-200 border-gray-400 text-gray-800 shadow-gray-200'
  };

  return (
    <div
      id={id}
      className={`${sizeClasses[size]} ${colorClasses[color]} relative border-2 rounded-lg flex flex-col items-center justify-center gap-0.5 font-semibold shadow-md hover:scale-105 transition-all duration-300 animate-fade-in p-1.5`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {Icon && <Icon size={size === 'sm' ? 14 : size === 'md' ? 16 : 18} strokeWidth={2.5} />}
      <span className="text-center leading-tight font-bold">{name}</span>
    </div>
  );
};

// Connection Arrow Component
const ConnectionArrow = ({ label, vertical = false, color = 'blue', delay = 0, dashed = false }) => {
  const colorClasses = {
    blue: 'text-blue-500',
    green: 'text-green-500',
    purple: 'text-purple-500',
    orange: 'text-orange-500',
    red: 'text-red-500',
    yellow: 'text-yellow-600',
    gray: 'text-gray-500'
  };

  if (vertical) {
    return (
      <div className={`flex flex-col items-center gap-0.5 animate-fade-in`} style={{ animationDelay: `${delay}ms` }}>
        <div className={`${dashed ? 'border-dashed' : ''} border-l-2 h-4 ${colorClasses[color].replace('text-', 'border-')}`}></div>
        <ArrowDown className={`${colorClasses[color]} animate-bounce-slow`} size={20} strokeWidth={2.5} />
        {label && <span className="text-[10px] font-semibold text-gray-700 bg-white px-1.5 py-0.5 rounded shadow-sm">{label}</span>}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-0.5 animate-fade-in`} style={{ animationDelay: `${delay}ms` }}>
      <div className={`${dashed ? 'border-dashed' : ''} border-t-2 w-4 ${colorClasses[color].replace('text-', 'border-')}`}></div>
      <ArrowRight className={`${colorClasses[color]} animate-flow-right`} size={20} strokeWidth={2.5} />
      {label && <span className="text-[10px] font-semibold text-gray-700 bg-white px-1.5 py-0.5 rounded shadow-sm whitespace-nowrap">{label}</span>}
    </div>
  );
};

// Account/VPC Container
const CloudContainer = ({ title, children, color = 'blue', delay = 0 }) => {
  const borderColors = {
    blue: 'border-blue-300 bg-blue-50/30',
    green: 'border-green-300 bg-green-50/30',
    purple: 'border-purple-300 bg-purple-50/30',
    orange: 'border-orange-300 bg-orange-50/30',
    red: 'border-red-300 bg-red-50/30'
  };

  return (
    <div
      className={`relative border-2 ${borderColors[color]} rounded-xl p-3 animate-fade-in backdrop-blur-sm`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="absolute -top-2.5 left-3 px-2 py-0.5 bg-white border-2 border-inherit rounded-md">
        <span className={`text-[10px] font-bold text-${color}-700 uppercase tracking-wide`}>{title}</span>
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
};

const ProjectFlowchart = ({ flowchart, projectId }) => {
  if (!flowchart) return null;

  // Project-specific architecture diagrams
  const renderArchitecture = () => {
    switch(projectId) {
      case 1: // Distributed Monitoring Platform - Using React Flow
        return (
          <div className="w-full">
            <Project1FlowDiagram />
          </div>
        );

      case 10001: // Distributed Monitoring Platform - Old React version (backup)
        return (
          <div className="space-y-2 p-2 relative">
            {/* ========== TOP: ACCOUNT BOUNDARIES ========== */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="border-2 border-blue-300 rounded-lg p-2 bg-blue-50/30">
                <div className="text-[9px] font-bold text-blue-700 text-center">Monitoring Service Account Boundary</div>
              </div>
              <div className="border-2 border-purple-300 rounded-lg p-2 bg-purple-50/30">
                <div className="text-[9px] font-bold text-purple-700 text-center">Private Connectivity Layer</div>
                <div className="text-[8px] text-center text-purple-600 mt-0.5">TGW / VPC Peering / VPN</div>
              </div>
              <div className="border-2 border-orange-300 rounded-lg p-2 bg-orange-50/30">
                <div className="text-[9px] font-bold text-orange-700 text-center">Customer Account Boundary</div>
                <div className="text-[8px] text-center text-orange-600 mt-0.5">30+ accounts pattern</div>
              </div>
            </div>

            {/* ========== MAIN LAYOUT: 2 COLUMNS WITH CONNECTIONS ========== */}
            <div className="grid grid-cols-2 gap-8 relative">

              {/* ========== LEFT: MONITORING SERVICE ACCOUNT ========== */}
              <div className="border-2 border-blue-400 rounded-xl p-3 bg-gradient-to-br from-blue-50 to-blue-100/50">
                <h4 className="text-[10px] font-bold text-blue-800 mb-2 text-center border-b-2 border-blue-300 pb-1">
                  MONITORING SERVICE ACCOUNT
                </h4>

                {/* Monitoring VPC Boundary */}
                <div className="border-2 border-blue-300 rounded-lg p-2 bg-white/60 mb-2">
                  <div className="text-[8px] font-bold text-blue-700 mb-1.5">Monitoring VPC Boundary</div>

                  {/* Private Runtime Subnets */}
                  <div className="border border-blue-200 rounded p-1.5 bg-blue-50/40 mb-1.5">
                    <div className="text-[7px] font-bold text-blue-600 mb-1">Private Runtime Subnets</div>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-center gap-1">
                        <ServiceNode name="Config Sync" icon={Workflow} color="purple" delay={0} size="sm" />
                      </div>
                      <div className="text-[7px] text-gray-600 text-center">Syncs Bitbucket configs</div>

                      {/* Flow Arrow Down */}
                      <div className="flex justify-center my-1">
                        <div className="flex flex-col items-center">
                          <ArrowDown className="text-blue-500 animate-bounce-slow" size={14} strokeWidth={2.5} />
                          <span className="text-[6px] text-blue-600 font-bold">Apply</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-center gap-1">
                        <ServiceNode name="ECS Cluster" icon={Container} color="blue" delay={25} size="sm" />
                      </div>
                      <div className="flex flex-wrap justify-center gap-1">
                        <ServiceNode name="Uptime Kuma" icon={Eye} color="blue" delay={50} size="sm" />
                      </div>
                      <div className="text-[7px] text-gray-600 text-center">Container per customer</div>

                      {/* Side arrow to Database */}
                      <div className="flex justify-center items-center gap-1 my-1">
                        <span className="text-[6px] text-green-600 font-bold">Stores →</span>
                        <ArrowDown className="text-green-500" size={12} strokeWidth={2} />
                      </div>
                    </div>
                  </div>

                  {/* Flow Arrow Down */}
                  <div className="flex justify-center my-1">
                    <div className="flex flex-col items-center">
                      <ArrowDown className="text-purple-500 animate-bounce-slow" size={16} strokeWidth={2.5} />
                      <span className="text-[6px] text-purple-600 font-bold">Outbound via NAT</span>
                    </div>
                  </div>

                  {/* NAT Gateway */}
                  <div className="border border-blue-200 rounded p-1.5 bg-blue-50/40 mb-1.5">
                    <div className="text-[7px] font-bold text-blue-600 mb-1">NAT Gateway</div>
                    <div className="flex justify-center">
                      <ServiceNode name="NAT GW" icon={Network} color="purple" delay={75} size="sm" />
                    </div>
                    <div className="text-[7px] text-gray-600 text-center mt-0.5">Outbound control</div>
                  </div>

                  {/* Database */}
                  <div className="border border-blue-200 rounded p-1.5 bg-blue-50/40">
                    <div className="text-[7px] font-bold text-blue-600 mb-1">Uptime Kuma Database</div>
                    <div className="flex justify-center gap-1">
                      <ServiceNode name="RDS/EBS" icon={Database} color="green" delay={100} size="sm" />
                    </div>
                    <div className="text-[7px] text-gray-600 text-center mt-0.5">Persistent Storage</div>
                  </div>
                </div>
              </div>

              {/* ========== RIGHT: CUSTOMER ACCOUNT ========== */}
              <div className="border-2 border-orange-400 rounded-xl p-3 bg-gradient-to-br from-orange-50 to-orange-100/50">
                <h4 className="text-[10px] font-bold text-orange-800 mb-2 text-center border-b-2 border-orange-300 pb-1">
                  CUSTOMER ACCOUNT (Representative of 30+)
                </h4>

                {/* Customer VPC */}
                <div className="border-2 border-orange-300 rounded-lg p-2 bg-white/60">

                  {/* Internal Load Balancer */}
                  <div className="border border-orange-200 rounded p-1.5 bg-orange-50/40 mb-1.5">
                    <div className="text-[7px] font-bold text-orange-600 mb-1">Internal Load Balancer</div>
                    <div className="flex justify-center">
                      <ServiceNode name="Internal ALB" icon={Network} color="orange" delay={125} size="sm" />
                    </div>
                    <div className="text-[7px] text-gray-600 text-center mt-0.5">Private endpoints</div>
                  </div>

                  {/* Flow Arrow Down */}
                  <div className="flex justify-center my-1">
                    <ArrowDown className="text-orange-500 animate-bounce-slow" size={16} strokeWidth={2.5} />
                  </div>

                  {/* Private Application Subnets */}
                  <div className="border border-orange-200 rounded p-1.5 bg-orange-50/40 mb-1.5">
                    <div className="text-[7px] font-bold text-orange-600 mb-1">Private Application Subnets</div>
                    <div className="space-y-1">
                      <div className="flex flex-wrap justify-center gap-1">
                        <ServiceNode name="Web/APIs" icon={Globe} color="orange" delay={150} size="sm" />
                        <ServiceNode name="GraphQL" icon={FileCode} color="orange" delay={175} size="sm" />
                      </div>
                      <div className="flex flex-wrap justify-center gap-1">
                        <ServiceNode name="Keycloak" icon={Lock} color="orange" delay={200} size="sm" />
                      </div>
                    </div>
                  </div>

                  {/* Flow Arrows Down */}
                  <div className="flex justify-center gap-4 my-1">
                    <div className="flex flex-col items-center">
                      <ArrowDown className="text-green-500 animate-bounce-slow" size={16} strokeWidth={2.5} />
                      <span className="text-[6px] text-green-600 font-bold">DB Queries</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <ArrowDown className="text-red-500 animate-bounce-slow" size={16} strokeWidth={2.5} />
                      <span className="text-[6px] text-red-600 font-bold">Metrics</span>
                    </div>
                  </div>

                  {/* Databases */}
                  <div className="border border-orange-200 rounded p-1.5 bg-orange-50/40 mb-1.5">
                    <div className="text-[7px] font-bold text-orange-600 mb-1">Private Data Subnets</div>
                    <div className="flex flex-wrap justify-center gap-1">
                      <ServiceNode name="RDS" icon={Database} color="green" delay={225} size="sm" />
                      <ServiceNode name="MongoDB" icon={Database} color="green" delay={250} size="sm" />
                    </div>
                  </div>

                  {/* Private Observability */}
                  <div className="border border-red-200 rounded p-1.5 bg-red-50/40">
                    <div className="text-[7px] font-bold text-red-600 mb-1">Private Observability</div>
                    <div className="space-y-1">
                      <div className="flex flex-wrap justify-center items-center gap-0.5 text-[7px]">
                        <ServiceNode name="CW" icon={Activity} color="red" delay={275} size="sm" />
                        <span className="text-red-500">→</span>
                        <ServiceNode name="SNS" icon={Zap} color="yellow" delay={300} size="sm" />
                        <span className="text-yellow-500">→</span>
                        <ServiceNode name="SQS" icon={Box} color="purple" delay={325} size="sm" />
                      </div>
                      <div className="flex justify-center">
                        <ServiceNode name="Batch EC2" icon={Server} color="blue" delay={350} size="sm" />
                      </div>
                      <div className="text-[7px] text-gray-600 text-center">Alarm validation ON/OFF</div>

                      {/* Flow Arrow Down */}
                      <div className="flex justify-center my-1">
                        <ArrowDown className="text-green-500 animate-bounce-slow" size={14} strokeWidth={2.5} />
                      </div>

                      <div className="flex flex-wrap justify-center gap-1">
                        <ServiceNode name="Jira" icon={FileCode} color="green" delay={375} size="sm" />
                        <ServiceNode name="Slack" icon={Zap} color="green" delay={400} size="sm" />
                      </div>
                    </div>
                  </div>

                  {/* VPC Endpoints */}
                  <div className="border border-purple-200 rounded p-1.5 bg-purple-50/40 mt-1.5">
                    <div className="text-[7px] font-bold text-purple-600 mb-1">Private Edge Subnets</div>
                    <div className="flex justify-center">
                      <ServiceNode name="VPC Endpoints" icon={Network} color="purple" delay={425} size="sm" />
                    </div>
                    <div className="text-[7px] text-gray-600 text-center mt-0.5">CloudWatch, SSM, S3</div>
                  </div>

                </div>
              </div>

              {/* ========== CONNECTION ARROWS BETWEEN COLUMNS ========== */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="flex flex-col items-center gap-2">
                  {/* Arrow to Customer */}
                  <div className="flex items-center gap-1 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full border-2 border-purple-400 shadow-lg">
                    <ArrowRight className="text-purple-600 animate-flow-right" size={18} strokeWidth={3} />
                    <span className="text-[8px] font-bold text-purple-700">Health Checks</span>
                  </div>
                  {/* Arrow from Customer */}
                  <div className="flex items-center gap-1 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full border-2 border-red-400 shadow-lg">
                    <span className="text-[8px] font-bold text-red-700">Metrics/Logs</span>
                    <ArrowRight className="text-red-600 animate-flow-right" size={18} strokeWidth={3} />
                  </div>
                </div>
              </div>

            </div>

            {/* ========== BOTTOM: DATA FLOWS ========== */}
            <div className="mt-3 pt-2 border-t-2 border-gray-300">
              <div className="text-center mb-1.5">
                <span className="text-[9px] font-bold text-gray-700 bg-gray-100 px-2 py-1 rounded border border-gray-300">
                  Data Flows
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[7px]">
                {/* Active Monitoring */}
                <div className="p-1.5 bg-blue-50 border border-blue-200 rounded">
                  <div className="font-bold text-blue-700 mb-0.5">① Active Monitoring</div>
                  <div className="flex flex-wrap items-center gap-0.5">
                    <span className="bg-blue-200 px-1 py-0.5 rounded">Uptime Kuma</span>
                    <span>→</span>
                    <span className="bg-purple-200 px-1 py-0.5 rounded">NAT</span>
                    <span>→</span>
                    <span className="bg-orange-200 px-1 py-0.5 rounded">Customer</span>
                  </div>
                </div>

                {/* Native Monitoring */}
                <div className="p-1.5 bg-red-50 border border-red-200 rounded">
                  <div className="font-bold text-red-700 mb-0.5">② Native Monitoring</div>
                  <div className="flex flex-wrap items-center gap-0.5">
                    <span className="bg-red-200 px-1 py-0.5 rounded">CloudWatch</span>
                    <span>→</span>
                    <span className="bg-yellow-200 px-1 py-0.5 rounded">SNS</span>
                    <span>→</span>
                    <span className="bg-purple-200 px-1 py-0.5 rounded">SQS</span>
                    <span>→</span>
                    <span className="bg-green-200 px-1 py-0.5 rounded">Alerts</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        );

      case 2: // Ubuntu Infrastructure & STIG Compliance
        return (
          <div className="w-full">
            <Project2FlowDiagram />
          </div>
        );

      case 3: // Data Backup & Restore Automation
        return (
          <div className="w-full">
            <Project3FlowDiagram />
          </div>
        );

      case 4: // Software Approval System
        return (
          <div className="w-full">
            <Project4FlowDiagram />
          </div>
        );

      case 5: // ElasticSearch Master Search Engine
        return (
          <div className="w-full">
            <Project5FlowDiagram />
          </div>
        );

      case 6: // Batch Service Pipeline
        return (
          <div className="w-full">
            <Project6FlowDiagram />
          </div>
        );
      case 7: // WeldWise Coach (MLOps)
        return (
          <div className="w-full">
            <Project7FlowDiagram />
          </div>
        );
      case 8: // K8s Forecasting Platform (NeuralProphet)
        return (
          <div className="w-full">
            <Project8FlowDiagram />
          </div>
        );
      case 9: // nibble - Open Source Go Library
        return (
          <div className="w-full">
            <Project9FlowDiagram />
          </div>
        );
      case 10: // MCP Security Scanning Report
        return (
          <div className="w-full">
            <Project10FlowDiagram />
          </div>
        );
      case 11: // Multi-Tenant Keycloak IAM Modernisation
        return (
          <div className="w-full">
            <Project11FlowDiagram />
          </div>
        );
      case 12: // Cloud Data Lakehouse on AWS
        return (
          <div className="w-full">
            <Project12FlowDiagram />
          </div>
        );
      case 13: // AI Platform Cloud Portability
        return (
          <div className="w-full">
            <Project13FlowDiagram />
          </div>
        );

      default:
        return <div className="text-center text-gray-500">Architecture diagram not available</div>;
    }
  };

  return (
    <div className={`bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 rounded-xl border-2 border-gray-200 ${projectId === 1 ? 'p-0' : 'p-3 md:p-4'}`}>
      {renderArchitecture()}

      {/* Custom CSS for Animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes flow-right {
          0%, 100% {
            transform: translateX(0);
            opacity: 0.5;
          }
          50% {
            transform: translateX(8px);
            opacity: 1;
          }
        }

        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }

        @keyframes pulse-slow {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
        }

        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
          opacity: 0;
        }

        .animate-flow-right {
          animation: flow-right 2s ease-in-out infinite;
        }

        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }

        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ProjectFlowchart;
